import FormQuickNav from "../components/FormQuickNav.component";
import React, { useState, useEffect, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import {
  Box,
  Typography,
  Paper,
  Button,
  CircularProgress,
  Alert,
  Grid,
  Card,
  Divider,
  Chip,
  IconButton,
  Tooltip,
} from "@mui/material";
import {
  ArrowBack as ArrowBackIcon,
  Info as InfoIcon,
} from "@mui/icons-material";
import JsonFormRenderer from "../components/JsonFormRenderer.component";
import { getFormById } from "../reducers/collaborativeForm.reducer";
import { selectFormData, selectFormStatus } from "../selectors/collaborativeForm.selector";
import {
  selectUserId,
  selectOrganisationId,
  selectSupplierId,
} from "../selectors/login.selector";
import TopBreadcrumbs from "../components/topBreadcrumbs.component";
import LeftSideNavbar from "../components/leftSideNavbar.component";
import { PageStyles } from "../styles";

const FormDetailPage = () => {
  const { formId } = useParams();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  // Get required data for auto-save functionality
  const formConfig = useSelector(selectFormData);
  const formStatus = useSelector(selectFormStatus);
  const userId = useSelector(selectUserId);
  const organisationId = useSelector(selectOrganisationId);
  const supplierId = useSelector(selectSupplierId);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentSection, setCurrentSection] = useState("Mill");
  const [sectionStatuses, setSectionStatuses] = useState({});
  const scrollContainerRef = useRef(null);

  useEffect(() => {
    const loadForm = async () => {
      try {
        setLoading(true);
        setError(null);

        if (!formId) {
          setError("No form ID available");
          setLoading(false);
          return;
        }

        // Fetch form data by ID
        dispatch(getFormById({ formId }));
      } catch (err) {
        console.error("Error loading form:", err);
        setError("Failed to load form data");
      } finally {
        setLoading(false);
      }
    };

    loadForm();
  }, [dispatch, formId]);

  useEffect(() => {
    if (formConfig) {
      setLoading(false);
      // Initialize section statuses based on form sections
      if (formConfig.sections) {
        const initialStatuses = {};
        // Convert sections object to array and use correct property names
        Object.values(formConfig.sections).forEach((section) => {
          // Use section.title instead of section.name (based on API structure)
          initialStatuses[section.title] = "not-filled"; // gray
        });
        setSectionStatuses(initialStatuses);
      }
    }
  }, [formConfig]);

  const handleFormChange = (updatedFormData) => {
    // console.log("Form data changed:", updatedFormData);
    // Here you would typically save the form data to the backend
    // For now, we'll just log it
  };

  const handleBack = () => {
    // Navigate based on form status
    if (formStatus === "submitted") {
      // Navigate back to material page using the formId (which is the materialId)
      if (supplierId) {
        navigate(`/suppliers/${supplierId}/material/${formId}`);
      } else {
        navigate(`/material/${formId}`);
      }
    } else {
      // Navigate back to todo page
      navigate("/todo");
    }
  };

  if (loading) {
    return (
      <Box sx={PageStyles.containerBox}>
        <LeftSideNavbar />
        <Box sx={PageStyles.containerBoxExceptNavbar}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              height: "100vh",
            }}
          >
            <CircularProgress />
          </Box>
        </Box>
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={PageStyles.containerBox}>
        <LeftSideNavbar />
        <Box sx={PageStyles.containerBoxExceptNavbar}>
          <Box sx={{ p: 4 }}>
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
            <Button variant="outlined" onClick={handleBack}>
              Back to Forms
            </Button>
          </Box>
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={PageStyles.containerBox}>
      {/* <FormQuickNav scrollContainerRef={scrollContainerRef} /> */}
      <Box sx={PageStyles.containerBoxExceptNavbar} ref={scrollContainerRef}>
        <Box sx={{
          ...PageStyles.firstSectionWithBreadcrumbsAndName,
          position: "sticky",
          top: 0,
          zIndex: 1000,
          borderBottom: "1px solid rgba(234, 236, 240, 1)",
        }}>
          {/* <TopBreadcrumbs /> */}
          <Grid container alignItems="center" justifyContent="space-between">
            <Grid item>
              <Typography gutterBottom sx={PageStyles.name}>
                Reformation Traceability Form
              </Typography>
            </Grid>
          </Grid>
        </Box>

        <Grid container spacing={2} sx={PageStyles.secondSectionGrid}>
          <Grid item xs={12} md={2}>
            <Box
              sx={{
                position: { xs: "static", md: "sticky" },
                top: { md: 96 },
                display: "flex",
                flexDirection: "column",
                gap: 2,
                zIndex: 1,
              }}
            >
              <Button
                variant="contained"
                startIcon={<ArrowBackIcon />}
                onClick={handleBack}
                sx={{
                  width: "100%",
                  textTransform: "none",
                  backgroundColor: "#2D3282",
                  borderRadius: "8px",
                  padding: "10px 16px",
                  fontSize: "14px",
                  fontWeight: 500,
                  "&:hover": {
                    backgroundColor: "#1E1F5C",
                  },
                }}
              >
                {formStatus === "submitted" ? "Back to Material Page" : "Back to To Do"}
              </Button>
              <FormQuickNav scrollContainerRef={scrollContainerRef} />
            </Box>
          </Grid>
          <Grid item xs={12} md={10}>
            <Card sx={PageStyles.cardForTabsAndBelow}>
              {formConfig ? (
                <Box>
                  <JsonFormRenderer
                    formConfig={formConfig}
                    onFormChange={handleFormChange}
                    formId={formId}
                    userId={userId}
                    organisationId={organisationId}
                  />
                </Box>
              ) : (
                <Box sx={{ 
                  height: "50vh", 
                  display: "flex", 
                  alignItems: "center",
                  justifyContent: "center"
                }}>
                  <Paper sx={{ p: 4, textAlign: "center" }}>
                    <Typography variant="h6" color="text.secondary">
                      Form configuration not available
                    </Typography>
                  </Paper>
                </Box>
              )}
            </Card>
          </Grid>
        </Grid>
      </Box>
    </Box>
  );
};

export default FormDetailPage;
